import React, { useEffect, useState } from 'react';

import * as leaderboard from '@/api/leaderboard.service';

import Typography from '@/ui-components/Typography';
import Loading from '@/ui-components/Loading';

import useBem from '@/use/useBem';

import LeaderboardItem, { LeaderboardItemProps } from './LeaderboardItem';

type LeaderboardUser = Omit<LeaderboardItemProps, 'rank'>;

const Leaderboard = () => {
  const { bem } = useBem('leaderboard');
  const [users, setUsers] = useState<LeaderboardUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchLeaderboard() {
      try {
        const { data } = await leaderboard.getLeaderboard();
        if (!cancelled) setUsers(data.users ?? []);
      } catch {
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchLeaderboard();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <Loading />;
  }

  if (users.length === 0) {
    return (
      <div className={bem('wrap')}>
        <Typography variant="l" className={bem('h')}>
          Leaderboard
        </Typography>
        <Typography variant="m">No scores yet or could not load.</Typography>
      </div>
    );
  }

  return (
    <div className={bem('wrap')}>
      <Typography variant="l" className={bem('h')}>
        Leaderboard
      </Typography>

      <div className={bem('table-wrap')}>
        <table className={bem('table')}>
          <thead>
            <tr>
              <th className={bem('rank')}>
                <Typography variant="th" component="span">
                  Rank
                </Typography>
              </th>
              <th className={bem('name')}>
                <Typography variant="th" component="span">
                  Name
                </Typography>
              </th>
              <th className={bem('score')}>
                <Typography variant="th" component="span">
                  Score
                </Typography>
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((player, index) => (
              <LeaderboardItem
                key={player.id ?? index}
                {...player}
                rank={index + 1}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Leaderboard;
